// <copyright file="quote-item-version-recheck.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import type { QuoteItemContainer } from '../quote-item-container';
import type { QuoteItemContainerV6Data } from './quote-item-container-expanded-v6-data';
import type { V6QuoteItemSupplierInfo } from '../../../../webmodule-common/interop/webmodule-interop';

/** Raised on the page once a customer code flush has reloaded the suppliers named in it (SUPVER-010). */
export const customerCodeFlushedPageEvent = 'wm-customer-code-flushed';

export interface CustomerCodeFlushedPageEventDetail {
  supplierIds: string[];
}

/**
 * After a customer code flush reloads the suppliers (SUPVER-010), an open quote's items still hold the version
 * state worked out when they were first read. This works it out again, against the reloaded suppliers, for each
 * item under one of the flushed suppliers, so the out-of-date indicators show without a page reload. Only the
 * cached supplier state is replaced: the item data, and any unsaved edit in it, is not touched.
 * Kept free of the V6 module graph so it can be tested on its own; the caller passes the resolver.
 * @returns how many items were rechecked
 */
export function recheckQuoteItemVersions(
  containers: Iterable<QuoteItemContainer>,
  supplierIds: Iterable<string>,
  resolveSupplier: (v6Item: object) => V6QuoteItemSupplierInfo
): number {
  const flushed = new Set([...supplierIds].map(x => x.toLowerCase()));
  let rechecked = 0;
  for (const container of containers) {
    const cache = container.clientSideCache as QuoteItemContainerV6Data | undefined;
    // An item not read yet has no cached state: it is worked out against the reloaded suppliers when it is.
    if (!cache?.v6Item || !cache.supplier) continue;
    if (!flushed.has(cache.supplier.supplierId.toLowerCase())) continue;
    try {
      cache.supplier = resolveSupplier(cache.v6Item);
      rechecked++;
    } catch (e) {
      // One item that cannot be worked out keeps its old state; the rest of the quote is still rechecked.
      console.warn('Could not recheck a quote item after a customer code flush', e);
    }
  }
  return rechecked;
}
